import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Truck, Package, MapPin, User, Phone, Calendar, 
  CreditCard, Smartphone, CheckCircle, ArrowLeft
} from 'lucide-react';
import { toast } from '../hooks/use-toast';
import api from '../utils/api';

const LogisticsBook = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { quote, formData: cargoData, distanceKm } = location.state || {};

  const [loading, setLoading] = useState(false);
  const [bookingComplete, setBookingComplete] = useState(false);
  const [bookingResult, setBookingResult] = useState(null);
  
  const [isScheduled, setIsScheduled] = useState(false);
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  
  const [paymentMethod, setPaymentMethod] = useState('cash');
  
  const [contactInfo, setContactInfo] = useState({
    sender_name: '',
    sender_phone: '',
    receiver_name: '',
    receiver_phone: ''
  });

  // Check if user is authenticated
  useEffect(() => {
    const token = localStorage.getItem('tekstar_token');
    if (!token) {
      toast({
        title: 'Login Required',
        description: 'Please login to book cargo transport',
        variant: 'destructive'
      });
      navigate('/login', { state: { from: '/logistics' } });
    }
  }, [navigate]);

  if (!quote || !cargoData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="p-6 text-center">
          <p className="text-gray-600 mb-4">No booking data found</p>
          <Button onClick={() => navigate('/logistics')}>
            Start New Booking
          </Button>
        </Card>
      </div>
    );
  }

  const handleBooking = async () => {
    // Validate contact info
    if (!contactInfo.sender_name || !contactInfo.sender_phone || 
        !contactInfo.receiver_name || !contactInfo.receiver_phone) {
      toast({
        title: 'Missing Information',
        description: 'Please fill in all contact details',
        variant: 'destructive'
      });
      return;
    }

    // Validate scheduled booking
    if (isScheduled && (!scheduledDate || !scheduledTime)) {
      toast({
        title: 'Missing Schedule',
        description: 'Please select a date and time for scheduled pickup',
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);
    try {
      const bookingData = {
        pickup_address: cargoData.pickup_address,
        dropoff_address: cargoData.dropoff_address,
        pickup_location: cargoData.pickup_location || { latitude: -15.4167, longitude: 28.2833 },
        dropoff_location: cargoData.dropoff_location || { latitude: -15.5000, longitude: 28.3000 },
        cargo: {
          description: cargoData.description,
          weight_kg: parseFloat(cargoData.weight_kg),
          length_cm: cargoData.length_cm ? parseFloat(cargoData.length_cm) : null,
          width_cm: cargoData.width_cm ? parseFloat(cargoData.width_cm) : null,
          height_cm: cargoData.height_cm ? parseFloat(cargoData.height_cm) : null,
          category: cargoData.category,
          special_instructions: cargoData.special_instructions || null
        },
        vehicle_type: quote.vehicle_type,
        scheduled_pickup: isScheduled ? `${scheduledDate}T${scheduledTime}:00` : null,
        sender_name: contactInfo.sender_name,
        sender_phone: contactInfo.sender_phone,
        receiver_name: contactInfo.receiver_name,
        receiver_phone: contactInfo.receiver_phone,
        payment_method: paymentMethod,
        distance_km: distanceKm,
        quoted_price: quote.total_price
      };

      const response = await api.post('/logistics/book', bookingData);
      
      setBookingResult(response.data.booking);
      setBookingComplete(true);
      
      toast({
        title: 'Booking Confirmed!',
        description: `Tracking Code: ${response.data.booking.trackingCode}`
      });
    } catch (error) {
      toast({
        title: 'Booking Failed',
        description: error.response?.data?.detail || 'Please try again',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  // Booking Success Screen
  if (bookingComplete && bookingResult) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full p-6 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="h-10 w-10 text-green-500" />
          </div>
          <h1 className="text-2xl font-bold text-green-700 mb-2">Booking Confirmed!</h1>
          <p className="text-gray-600 mb-4">Your cargo transport has been booked successfully</p>
          
          <div className="bg-gray-50 rounded-lg p-4 mb-4">
            <p className="text-sm text-gray-500">Tracking Code</p>
            <p className="text-2xl font-mono font-bold text-orange-600">
              {bookingResult.trackingCode}
            </p>
          </div>
          
          <div className="text-left space-y-2 mb-6">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Status</span>
              <span className="font-medium capitalize">{bookingResult.status}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Amount</span>
              <span className="font-medium">K{bookingResult.price?.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Booking Type</span>
              <span className="font-medium">{bookingResult.isScheduled ? 'Scheduled' : 'Instant'}</span>
            </div>
          </div>
          
          <div className="space-y-2">
            <Button 
              className="w-full bg-orange-500 hover:bg-orange-600"
              onClick={() => navigate('/logistics/track', { state: { trackingCode: bookingResult.trackingCode } })}
            >
              Track Shipment
            </Button>
            <Button 
              variant="outline" 
              className="w-full"
              onClick={() => navigate('/logistics/my-shipments')}
            >
              View All Shipments
            </Button>
            <Button 
              variant="ghost" 
              className="w-full"
              onClick={() => navigate('/logistics')}
            >
              Book Another
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-500 to-amber-500 text-white p-6">
        <div className="max-w-4xl mx-auto">
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-orange-100 hover:text-white mb-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>
          <h1 className="text-2xl font-bold">Confirm Booking</h1>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4 space-y-4">
        {/* Order Summary */}
        <Card className="p-4">
          <h2 className="font-semibold mb-3 flex items-center gap-2">
            <Package className="h-5 w-5 text-orange-500" />
            Order Summary
          </h2>
          
          <div className="space-y-3">
            <div className="flex items-center gap-3 p-3 bg-orange-50 rounded-lg">
              <span className="text-2xl">{quote.vehicle_icon}</span>
              <div className="flex-1">
                <p className="font-medium">{quote.vehicle_name}</p>
                <p className="text-sm text-gray-500">{quote.estimated_duration}</p>
              </div>
              <p className="text-xl font-bold text-orange-600">K{quote.total_price.toFixed(2)}</p>
            </div>
            
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-500">From</p>
                <p className="font-medium">{cargoData.pickup_address}</p>
              </div>
              <div>
                <p className="text-gray-500">To</p>
                <p className="font-medium">{cargoData.dropoff_address}</p>
              </div>
            </div>
            
            <div className="text-sm">
              <p className="text-gray-500">Cargo</p>
              <p className="font-medium">{cargoData.description} ({cargoData.weight_kg} kg)</p>
            </div>
          </div>
        </Card>

        {/* Contact Information */}
        <Card className="p-4">
          <h2 className="font-semibold mb-3 flex items-center gap-2">
            <User className="h-5 w-5 text-orange-500" />
            Contact Information
          </h2>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Sender Name *</Label>
                <Input
                  value={contactInfo.sender_name}
                  onChange={(e) => setContactInfo({ ...contactInfo, sender_name: e.target.value })}
                  placeholder="Your name"
                  data-testid="sender-name"
                />
              </div>
              <div>
                <Label>Sender Phone *</Label>
                <Input
                  value={contactInfo.sender_phone}
                  onChange={(e) => setContactInfo({ ...contactInfo, sender_phone: e.target.value })}
                  placeholder="+260..."
                  data-testid="sender-phone"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Receiver Name *</Label>
                <Input
                  value={contactInfo.receiver_name}
                  onChange={(e) => setContactInfo({ ...contactInfo, receiver_name: e.target.value })}
                  placeholder="Receiver's name"
                  data-testid="receiver-name"
                />
              </div>
              <div>
                <Label>Receiver Phone *</Label>
                <Input
                  value={contactInfo.receiver_phone}
                  onChange={(e) => setContactInfo({ ...contactInfo, receiver_phone: e.target.value })}
                  placeholder="+260..."
                  data-testid="receiver-phone"
                />
              </div>
            </div>
          </div>
        </Card>

        {/* Scheduling */}
        <Card className="p-4">
          <h2 className="font-semibold mb-3 flex items-center gap-2">
            <Calendar className="h-5 w-5 text-orange-500" />
            Pickup Schedule
          </h2>
          
          <div className="space-y-3">
            <div className="flex gap-2">
              <Button
                variant={!isScheduled ? 'default' : 'outline'}
                onClick={() => setIsScheduled(false)}
                className={!isScheduled ? 'bg-orange-500 hover:bg-orange-600' : ''}
              >
                Pickup Now
              </Button>
              <Button
                variant={isScheduled ? 'default' : 'outline'}
                onClick={() => setIsScheduled(true)}
                className={isScheduled ? 'bg-orange-500 hover:bg-orange-600' : ''}
              >
                Schedule Later
              </Button>
            </div>
            
            {isScheduled && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Pickup Date</Label>
                  <Input
                    type="date"
                    value={scheduledDate}
                    onChange={(e) => setScheduledDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>
                <div>
                  <Label>Pickup Time</Label>
                  <Input
                    type="time"
                    value={scheduledTime}
                    onChange={(e) => setScheduledTime(e.target.value)}
                  />
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* Payment Method */}
        <Card className="p-4">
          <h2 className="font-semibold mb-3 flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-orange-500" />
            Payment Method
          </h2>
          
          <div className="grid grid-cols-3 gap-2">
            <button
              onClick={() => setPaymentMethod('cash')}
              className={`p-3 rounded-lg border-2 text-center transition-all ${
                paymentMethod === 'cash'
                  ? 'border-orange-500 bg-orange-50'
                  : 'border-gray-200 hover:border-orange-200'
              }`}
              data-testid="payment-cash"
            >
              <CreditCard className="h-6 w-6 mx-auto mb-1 text-green-600" />
              <span className="text-sm font-medium">Cash</span>
            </button>
            
            <button
              onClick={() => setPaymentMethod('mtn_mobile_money')}
              className={`p-3 rounded-lg border-2 text-center transition-all ${
                paymentMethod === 'mtn_mobile_money'
                  ? 'border-orange-500 bg-orange-50'
                  : 'border-gray-200 hover:border-orange-200'
              }`}
              data-testid="payment-mtn"
            >
              <Smartphone className="h-6 w-6 mx-auto mb-1 text-yellow-500" />
              <span className="text-sm font-medium">MTN MoMo</span>
            </button>
            
            <button
              onClick={() => setPaymentMethod('airtel_money')}
              className={`p-3 rounded-lg border-2 text-center transition-all ${
                paymentMethod === 'airtel_money'
                  ? 'border-orange-500 bg-orange-50'
                  : 'border-gray-200 hover:border-orange-200'
              }`}
              data-testid="payment-airtel"
            >
              <Smartphone className="h-6 w-6 mx-auto mb-1 text-red-500" />
              <span className="text-sm font-medium">Airtel Money</span>
            </button>
          </div>
        </Card>

        {/* Confirm Button */}
        <Button 
          onClick={handleBooking}
          disabled={loading}
          className="w-full py-6 text-lg bg-orange-500 hover:bg-orange-600"
          data-testid="confirm-booking-btn"
        >
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
              Processing...
            </>
          ) : (
            <>
              <CheckCircle className="mr-2 h-5 w-5" />
              Confirm Booking - K{quote.total_price.toFixed(2)}
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default LogisticsBook;
